<script lang="ts">
	import Bao from '$lib/assets/bao.webp?enhanced';
	import Caitlin from '$lib/assets/caitlin.webp?enhanced';
	import James from '$lib/assets/james.webp?enhanced';
	import Tom from '$lib/assets/tom.webp?enhanced';
	import FadeIn3d from '$lib/components/fade-in-3d.svelte';
	import { Button } from '@hessianhq/ui';
	import Github from '$lib/icons/github.svelte';
	import Linkedin from '$lib/icons/linkedin.svelte';
	import * as m from '$lib/paraglide/messages';
	import {
		ArrowRight,
		LineChart,
		Presentation,
		Settings2,
		TrendingUp,
		Wallet
	} from '@lucide/svelte';
	import { onMount } from 'svelte';
	import HeroLiveDag from '../routes/(public)/(page)/hero-live-dag.svelte';
	import IntegrationsMarquee from '../routes/(public)/(page)/integrations-marquee.svelte';
	import SectionHeader from '../routes/(public)/(page)/section-header.svelte';
	import SplashShowcase from '../routes/(public)/(page)/splash-showcase.svelte';

	// Scroll-driven word reveal for the "fast eat the slow" pitch section.
	const pitchLede =
		"Every company is trying to automate knowledge work. They've bought the tools. They've run the pilots. Almost nothing has changed — the strategy still lives on a slide.";
	const pitchPunchline =
		'We build the systems that do the work, and work with you to get them into production.';
	const ledeWords = pitchLede.split(' ');
	const punchlineWords = pitchPunchline.split(' ');
	const totalPitchWords = ledeWords.length + punchlineWords.length;

	let pitchSection = $state<HTMLElement | null>(null);
	let pitchProgress = $state(0);
	// Race visualization sits at the bottom of the catchphrase section, so it needs
	// its own progress tied to a longer scroll window — otherwise pitchProgress
	// completes before the race is even visible on screen.
	let raceProgress = $state(0);

	// Scroll-coupled reveal for the "Our platform" intro — bridges the hero→intro
	// boundary with motion so the second section eases into view instead of jumping.
	let platformSection = $state<HTMLElement | null>(null);
	let platformProgress = $state(0);
	const platformHeadingReveal = $derived(platformProgress);
	const platformPreambleReveal = $derived(
		Math.max(0, Math.min(1, (platformProgress - 0.15) / 0.85))
	);

	// Scroll-coupled 3D reveal for the SplashShowcase dashboard preview — replaces
	// the one-shot FadeIn3d so the animation tracks the user's scroll instead of
	// firing once on mount.
	let splashSection = $state<HTMLElement | null>(null);
	let splashProgress = $state(0);

	// Scroll-driven word reveal for the testimonial.
	const testimonialQuote =
		'"Hessian has been critical to us implementing AI workflows internally within our organization. By automating the messy, ad-hoc workflows our team was never able to standardize (sales handoffs, competitive intel, internal ops), Hessian has let our reps focus solely on closing deals which is the most valuable thing they can be doing. They also saved us from having to hire an AI engineer internally."';
	const testimonialWords = testimonialQuote.split(' ');
	// Bold the two payoff phrases: "let our reps focus solely on closing deals"
	// (34–41) and "saved us from having to hire an AI engineer" (54–62).
	const testimonialBoldIndices = new Set([
		34, 35, 36, 37, 38, 39, 40, 41, 54, 55, 56, 57, 58, 59, 60, 61, 62
	]);
	let testimonialSection = $state<HTMLElement | null>(null);
	let testimonialProgress = $state(0);
	function testimonialReveal(idx: number) {
		const fadeWidth = 6 / testimonialWords.length;
		const wordCenter = fadeWidth / 2 + (idx / (testimonialWords.length - 1)) * (1 - fadeWidth);
		return Math.max(0, Math.min(1, (testimonialProgress - wordCenter) / fadeWidth + 0.5));
	}
	const testimonialReveals = $derived(testimonialWords.map((_, i) => testimonialReveal(i)));

	// Hero "scroll to see more" hint — fades out as the user starts scrolling.
	let scrollY = $state(0);
	const heroHintOpacity = $derived(Math.max(0, 1 - scrollY / 240));

	// Scroll-linked card reveal for the "How we work" section. Each card binds
	// to its own ref and computes progress from its own viewport position — so on
	// mobile (cards stacked vertically) the reveal fires as each card enters view,
	// instead of all three animating off-screen before the user reaches them.
	let howWeWorkCards = $state<(HTMLElement | null)[]>([null, null, null]);
	let cardReveals = $state([0, 0, 0]);
	// Accent appears in the back half of each card's reveal so the colored element
	// snaps into place while the card is settling — close enough to feel connected,
	// late enough to read as a deliberate punch.
	const accentScales = $derived(cardReveals.map((p) => Math.max(0, Math.min(1, (p - 0.5) / 0.45))));
	// Sub-progress helper for per-element animations within each card's reveal.
	const subRange = (p: number, start: number, end: number) =>
		Math.max(0, Math.min(1, (p - start) / (end - start)));

	// Per-card sub-element animation progresses. Each value drives a specific
	// element inside the card (lines drawing, nodes scaling, bars rising, etc.).
	const card1LinesP = $derived(subRange(cardReveals[0], 0.25, 0.6));
	const card1NodesP = $derived(subRange(cardReveals[0], 0.5, 0.8));
	const card2Row1P = $derived(subRange(cardReveals[1], 0.25, 0.45));
	const card2Row2P = $derived(subRange(cardReveals[1], 0.35, 0.55));
	const card2Row3P = $derived(subRange(cardReveals[1], 0.45, 0.65));
	const card2DivP = $derived(subRange(cardReveals[1], 0.55, 0.78));
	const card3TrackP = $derived(subRange(cardReveals[2], 0.2, 0.45));
	const card3VertP = $derived(subRange(cardReveals[2], 0.35, 0.6));
	const card3BarsBaseP = $derived(subRange(cardReveals[2], 0.45, 0.72));
	const card3BarPs = $derived(
		[0, 1, 2, 3, 4].map((i) => subRange(card3BarsBaseP, i * 0.1, 0.3 + i * 0.1))
	);
	const card3DotPs = $derived(
		[0, 1, 2].map((i) => Math.max(0, Math.min(1, (accentScales[2] - i * 0.25) / 0.4)))
	);

	function wordReveal(idx: number) {
		// Each word lights up over ~6 word-widths of scroll progress, so reveals overlap smoothly.
		// Word centers are inset by fadeWidth/2 on each end so the first/last words fully reveal
		// exactly at progress 0/1 instead of stalling at ~66%.
		// The body reveal runs at 2× pitchProgress so it completes in the first half of the
		// scroll window, leaving the catchphrase weight shift to continue through the back half.
		const fadeWidth = 6 / totalPitchWords;
		const wordCenter = fadeWidth / 2 + (idx / (totalPitchWords - 1)) * (1 - fadeWidth);
		const bodyProgress = Math.min(1, pitchProgress * 2);
		return Math.max(0, Math.min(1, (bodyProgress - wordCenter) / fadeWidth + 0.5));
	}

	const ledeReveals = $derived(ledeWords.map((_, i) => wordReveal(i)));
	const punchlineReveals = $derived(punchlineWords.map((_, i) => wordReveal(ledeWords.length + i)));

	// Catchphrase emphasis tracks the full pitch progress (no longer 2× boosted) so the
	// big/small/fast/slow weight shift unfolds across the whole scroll window instead
	// of finishing halfway through.
	const catchphraseEmphasis = $derived(pitchProgress);
	const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

	onMount(() => {
		// Users who prefer reduced motion get the final state for all scroll-linked
		// reveals — no listeners, no progress tweening, just the resolved layout.
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			pitchProgress = 1;
			raceProgress = 1;
			platformProgress = 1;
			splashProgress = 1;
			testimonialProgress = 1;
			cardReveals = [1, 1, 1];
			return;
		}

		const update = () => {
			scrollY = window.scrollY;
			const vh = window.innerHeight;
			if (pitchSection) {
				const rect = pitchSection.getBoundingClientRect();
				// Begin reveal once the section top has crossed 40% of viewport, but let
				// the reveal continue well past the top edge (end at -30% of viewport) so
				// the catchphrase weight shift and body reveal both unfold over a long,
				// readable scroll window instead of snapping.
				const start = vh * 0.4;
				const end = vh * -0.3;
				pitchProgress = Math.max(0, Math.min(1, (start - rect.top) / (start - end)));
				// Race lives at the bottom of the section, so it animates over a longer
				// window — completing only when the section has nearly scrolled past.
				const raceEnd = vh * -1.0;
				raceProgress = Math.max(0, Math.min(1, (start - rect.top) / (start - raceEnd)));
			}
			if (platformSection) {
				const rect = platformSection.getBoundingClientRect();
				// Begin reveal while the section is still 15% below the fold so it's already
				// part-way through fading in by the time it enters the viewport — keeps the
				// hero→intro boundary in continuous motion rather than a hard cut.
				const start = vh * 1.15;
				const end = vh * 0.6;
				platformProgress = Math.max(0, Math.min(1, (start - rect.top) / (start - end)));
			}
			if (splashSection) {
				const rect = splashSection.getBoundingClientRect();
				// Begin the 3D reveal while the dashboard preview is still below the fold
				// so by the time the top edge enters view, it's already in flight.
				const start = vh * 1.0;
				const end = vh * 0.35;
				splashProgress = Math.max(0, Math.min(1, (start - rect.top) / (start - end)));
			}
			if (testimonialSection) {
				const rect = testimonialSection.getBoundingClientRect();
				// Hold the words dim until the section has scrolled well into the
				// reading zone (top past 35% of viewport), and finish the reveal just
				// past the top edge.
				const start = vh * 0.35;
				const end = vh * -0.05;
				testimonialProgress = Math.max(0, Math.min(1, (start - rect.top) / (start - end)));
			}
			// How-we-work cards — each card tracks its OWN viewport position so
			// stacked mobile cards reveal as they enter view. On desktop the row's
			// shared y position keeps them animating roughly together; a small
			// per-card stagger offset gives them a slight sequential feel there.
			for (let i = 0; i < 3; i++) {
				const el = howWeWorkCards[i];
				if (!el) continue;
				const rect = el.getBoundingClientRect();
				const stagger = i * (vh * 0.04);
				const start = vh * 0.85 + stagger;
				const end = vh * 0.3 + stagger;
				cardReveals[i] = Math.max(0, Math.min(1, (start - rect.top) / (start - end)));
			}
		};
		update();
		window.addEventListener('scroll', update, { passive: true });
		window.addEventListener('resize', update);
		return () => {
			window.removeEventListener('scroll', update);
			window.removeEventListener('resize', update);
		};
	});
</script>

<main class="relative" style="perspective: 2000px;">
	<div
		class="mx-auto grid min-h-[100dvh] max-w-264 items-center gap-10 px-6 pt-24 pb-12 sm:px-10 lg:grid-cols-[1fr_1fr] lg:gap-14"
	>
		<!-- LEFT: kicker, heading, lede, CTAs -->
		<div>
			<FadeIn3d delay={50}>
				<a
					href="https://www.ycombinator.com"
					target="_blank"
					rel="noopener"
					class="text-primary mb-4 inline-flex items-center gap-2 rounded-md border border-[#fb651e] bg-[#fb651e]/10 py-[5px] pr-3 pl-1.5 font-mono text-[10.5px] tracking-[0.16em] uppercase no-underline transition-colors duration-200 hover:bg-[#fb651e]/[0.18]"
					aria-label="Backed by Y Combinator"
				>
					<svg
						class="block h-4 w-4 shrink-0 rounded-[3px]"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 34 34"
						aria-hidden="true"
					>
						<rect width="34" height="34" fill="#FB651E" />
						<path
							fill="#fff"
							d="m16.252 19.224-5.836-10.932h2.667l3.433 6.918c.053.123.114.251.185.383.07.132.132.269.185.41a.805.805 0 0 1 .079.145c.018.044.035.083.053.119.088.175.167.347.237.514.07.168.132.322.185.463.141-.3.295-.621.462-.964.168-.344.34-.7.515-1.07l3.486-6.918h2.482l-5.889 11.064v7.05h-2.244v-7.182Z"
						/>
					</svg>
					<span>Backed by Y Combinator</span>
				</a>
			</FadeIn3d>

			<FadeIn3d delay={100}>
				<h1
					class="text-primary mt-2 mb-6 font-semibold tracking-[-0.035em]"
					style="font-size: clamp(2rem, 4.5vw, 4rem); line-height: 1;"
				>
					Automate the operations<br />
					your team can't scale.
				</h1>
			</FadeIn3d>

			<FadeIn3d delay={250}>
				<p
					class="text-soft mt-8 mb-9 max-w-140 leading-[1.55]"
					style="font-size: clamp(17px, 1.4vw, 20px);"
				>
					{m.index_description_top()}
					<br />
					{m.index_description_bottom()}
				</p>
			</FadeIn3d>

			<FadeIn3d delay={400}>
				<div class="flex flex-wrap items-center gap-2.5">
					<Button
						href="https://calendar.app.google/6dgXubAvkt54JReg8"
						target="_blank"
						rel="noopener noreferrer"
						class="bg-primary text-background hover:bg-primary/90 inline-flex h-11 w-1/2 min-w-16 items-center justify-center gap-2 rounded-md px-6 text-[15px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)] transition-colors"
					>
						{m.index_announcement_button()}
						<ArrowRight class="size-3.5" />
					</Button>
				</div>
			</FadeIn3d>
		</div>

		<!-- RIGHT: product video -->
		<FadeIn3d delay={300}>
			<div
				class="border-border bg-background relative aspect-video w-full overflow-hidden rounded-lg border shadow-[0_1px_2px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.04)]"
			>
				<iframe
					class="absolute inset-0 h-full w-full"
					src="https://www.youtube.com/embed/7lmP5CyehD4"
					title="YouTube video player"
					frameborder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					referrerpolicy="strict-origin-when-cross-origin"
					allowfullscreen
				></iframe>
			</div>
		</FadeIn3d>
	</div>

	<!-- Subtle "scroll to see more" hint anchored to the hero's bottom edge — fades on scroll. -->
	<div
		class="text-soft-gray pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase transition-opacity duration-300 md:flex"
		style="opacity: {heroHintOpacity * 0.7};"
		aria-hidden="true"
	>
		<span>See more</span>
		<svg
			class="scroll-hint-arrow"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="1.5"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M12 5v14M5 12l7 7 7-7" />
		</svg>
	</div>
</main>

<!-- "Our platform" intro — frames the dashboard preview that follows. -->
<section bind:this={platformSection} class="mx-auto max-w-264 px-6 pt-10 sm:px-10 md:pt-14">
	<div class="grid items-end gap-8 lg:grid-cols-[1fr_minmax(240px,1fr)] lg:gap-16">
		<div
			style="opacity: {platformHeadingReveal}; transform: translate3d(0, {(1 -
				platformHeadingReveal) *
				28}px, 0);"
		>
			<div class="kicker mb-5">
				<span class="kicker-rule"></span>
				<span>Platform</span>
			</div>
			<h2
				class="text-primary m-0 font-semibold tracking-[-0.03em]"
				style="font-size: clamp(1.75rem, 3.4vw, 3rem); line-height: 1.05;"
			>
				Our platform,<br />
				<span class="serif-ital">built for you.</span>
			</h2>
		</div>
		<p
			class="text-soft m-0 max-w-110 leading-[1.55]"
			style="font-size: clamp(16px, 1.15vw, 18px); opacity: {platformPreambleReveal}; transform: translate3d(0, {(1 -
				platformPreambleReveal) *
				28}px, 0);"
		>
			Hessian is built end-to-end by us, to best serve you. We ship new features on top of the core
			continuously — designed to meet your needs and complement the solution providers you already
			rely on.
		</p>
	</div>
</section>

<!-- Desktop: full interactive dashboard preview. Scroll-linked 3D reveal —
	 mirrors FadeIn3d's transform/opacity arc but ties it to scroll progress. -->
<div bind:this={splashSection} class="bg-background hidden w-full overflow-hidden md:block">
	<!-- SplashShowcase carries its own 3D rotation + bg-background internally
		 (linear-gradient-bottom-fade-out rotate-x-[15deg] etc), so this wrapper
		 only needs to handle the scroll-coupled fade-in. -->
	<div class="relative mr-16" style="opacity: {splashProgress}; will-change: opacity;">
		<div class="relative -right-12 mt-12 min-w-full md:ml-12" style="min-height: 600px;">
			<SplashShowcase />
		</div>
	</div>
</div>

<!-- Mobile: the dashboard preview is too wide for small screens, so swap
	 in the same live-DAG widget used in the auth layout + hero as a
	 self-contained visual. -->
<div class="mb-8 block px-6 pt-8 md:hidden">
	<FadeIn3d delay={550} duration={900}>
		<div
			class="text-soft-gray mb-3 flex items-center justify-between font-mono text-[11px] tracking-[0.08em] uppercase"
		>
			<span>runz_2aisfyz4vz2anpsrqp5tga</span>
		</div>
		<HeroLiveDag />
	</FadeIn3d>
</div>

<IntegrationsMarquee />

<!-- Testimonial — vertically centered, with scroll-driven word reveal. -->
<section
	bind:this={testimonialSection}
	class="mx-auto flex min-h-[80dvh] max-w-264 flex-col justify-center px-6 pt-24 pb-10 sm:px-10"
>
	<div class="kicker mb-6">
		<span class="kicker-rule"></span>
		<span>Testimonial</span>
	</div>
	<figure class="m-0">
		<blockquote
			class="text-primary m-0 max-w-220 font-medium tracking-[-0.015em]"
			style="font-size: clamp(1.375rem, 2.4vw, 2rem); line-height: 1.35;"
		>
			{#each testimonialWords as word, i (i)}<span
					class={testimonialBoldIndices.has(i) ? 'font-extrabold' : ''}
					style="opacity: {0.22 + 0.78 * testimonialReveals[i]}"
					>{word}{i < testimonialWords.length - 1 ? ' ' : ''}</span
				>{/each}
		</blockquote>
		<figcaption class="text-soft-gray mt-8 flex items-center gap-3.5 text-sm">
			<div
				class="flex size-11 shrink-0 items-center justify-center rounded-full font-semibold text-white"
				aria-hidden="true"
			>
				<enhanced:img class="h-full w-full" src={Caitlin} alt="" />
			</div>
			<div>
				<div class="text-primary font-medium">Caitlin Leksana</div>
				<div>CEO, Fazeshift, Inc.</div>
			</div>
		</figcaption>
	</figure>
</section>

<section
	bind:this={pitchSection}
	class="mx-auto max-w-264 px-6 pt-10 pb-24 sm:px-10 md:pt-16 md:pb-32"
>
	<div class="kicker mb-8 md:mb-12">
		<span class="kicker-rule"></span>
		<span>Why now</span>
	</div>

	<div class="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
		<FadeIn3d delay={50}>
			<h2
				class="text-primary m-0 font-normal tracking-[-0.03em]"
				style="font-size: clamp(1.75rem, 3.4vw, 3rem); line-height: 1.05;"
			>
				It's not the <span
					style="font-weight: {lerp(400, 300, catchphraseEmphasis)}; opacity: {lerp(
						1,
						0.35,
						catchphraseEmphasis
					)};">big</span
				>
				that eat the
				<span style="font-weight: {lerp(400, 900, catchphraseEmphasis)};">small</span>.<br /><br />
				<span class="serif-ital"
					>It's the <span
						style="font-weight: {lerp(400, 900, catchphraseEmphasis)}; font-style: italic;"
						>fast</span
					>
					that eat the
					<span
						style="font-weight: {lerp(400, 300, catchphraseEmphasis)}; opacity: {lerp(
							1,
							0.35,
							catchphraseEmphasis
						)};">slow</span
					>.</span
				>
			</h2>
		</FadeIn3d>

		<div>
			<p class="text-soft m-0 leading-[1.6]" style="font-size: clamp(16px, 1.15vw, 18px);">
				{#each ledeWords as word, i (i)}<span style="opacity: {0.22 + 0.78 * ledeReveals[i]}"
						>{word}{i < ledeWords.length - 1 ? ' ' : ''}</span
					>{/each}
			</p>
			<p
				class="text-primary m-0 mt-5 leading-[1.55] font-medium"
				style="font-size: clamp(16px, 1.15vw, 18px);"
			>
				{#each punchlineWords as word, i (i)}<span
						style="opacity: {0.22 + 0.78 * punchlineReveals[i]}"
						>{word}{i < punchlineWords.length - 1 ? ' ' : ''}</span
					>{/each}
			</p>
		</div>
	</div>

	<!-- Pursuit race — implicit visualization beneath the catchphrase: two dots,
		 no labels, no tracks. Big slow dot inches forward while the small fast
		 dot races across. Driven by `raceProgress` which runs longer than the
		 catchphrase reveal so the race plays out while the section is visible. -->
	<div class="mt-14 max-w-140 md:mt-20">
		<!-- Big slow dot -->
		<div class="relative mb-10 h-12">
			<div
				class="bg-primary absolute top-0 -ml-6 size-12 rounded-full"
				style="left: {raceProgress * 22}%;"
			></div>
		</div>
		<!-- Small fast triangle — clip-path triangle pointing upward. -->
		<div class="relative h-2">
			<div
				class="bg-primary absolute top-0 -ml-1 size-2"
				style="left: {raceProgress * 96}%; clip-path: polygon(50% 0, 100% 100%, 0 100%);"
			></div>
		</div>
	</div>
</section>

<!-- <section class="mx-auto max-w-264 px-6 pt-24 sm:px-10 md:px-6 md:pt-32 xl:px-10">
	<SectionHeader
		num="02"
		kicker="Build"
		lede="Design agents and complex workflows as code. Connect tasks that run on Docker, Kubernetes, the cloud, or locally."
	>
		{#snippet title()}
			Workflows and agents,<br /><span class="serif-ital">running everywhere.</span>
		{/snippet}
	</SectionHeader>
</section> -->

<!-- <section class="mx-auto max-w-300 px-6 sm:px-10 md:mt-4">
	<div
		class="bg-border border-border grid grid-cols-1 gap-px overflow-hidden rounded-xl border md:grid-cols-6"
	>
		{#snippet hoverArrow()}
			<ArrowUpRight
				class="text-soft-gray pointer-events-none absolute top-5 right-5 size-4 -translate-x-1.5 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 md:top-7 md:right-7"
			/>
		{/snippet} -->

<!-- Row 1: Workflow DAG (span 3) + SDK (span 3) -->
<!-- <div class="group bg-background relative min-h-95 p-8 md:col-span-3 md:p-10">
			<WorkflowBento class="h-full" />
			{@render hoverArrow()}
		</div>

		<div class="group bg-background relative min-h-95 overflow-clip p-8 md:col-span-3 md:p-10">
			<SdkBento class="h-full" />
			{@render hoverArrow()}
		</div> -->

<!-- Row 2: Logs (span 4) + Security (span 2) -->
<!-- <div class="group bg-background relative min-h-105 p-8 md:col-span-4 md:p-10">
			<LogsShowcase class="h-full w-full" />
			{@render hoverArrow()}
		</div>

		<div class="group bg-background relative min-h-105 p-8 md:col-span-2 md:p-10">
			<SecurityShowcase className="h-full w-full" />
			{@render hoverArrow()}
		</div>
 -->
<!-- Row 3: Policies (span 2) + Local dev (span 4) -->
<!-- <div class="group bg-background relative min-h-105 p-8 md:col-span-2 md:p-10">
			<LocalDevBento class="h-full" />
			{@render hoverArrow()}
		</div>

		<div class="group bg-background relative min-h-105 overflow-clip p-8 md:col-span-4 md:p-10">
			<ToolPoliciesShowcase />
			{@render hoverArrow()}
		</div>
	</div>
</section> -->

<!-- Workflows — bento grid of example agent categories already in production. -->
<section class="mx-auto max-w-264 px-6 pt-24 pb-12 sm:px-10 md:pt-32 md:pb-16">
	<SectionHeader kicker="Workflows">
		{#snippet title()}
			Built. Shipped. Running.
		{/snippet}
	</SectionHeader>

	<!-- Bento grid — single rounded frame with hairline gaps between cells (bg-border
		 shows through the gap), so the four cards read as one merged shape. Spans
		 are asymmetric (4+2 then 2+4) to give the grid a "morph" feel. -->
	<div
		class="bg-border border-border mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-xl border md:mt-16 md:grid-cols-6"
	>
		<article class="bg-background min-h-72 p-8 md:col-span-4 md:p-10">
			<div class="text-soft-gray mb-8">
				<TrendingUp class="size-7" strokeWidth={1.5} />
			</div>
			<h3 class="text-primary m-0 text-xl leading-tight font-semibold tracking-[-0.02em]">
				Sales agents.
			</h3>
			<p class="text-soft m-0 mt-3 max-w-110 leading-[1.55]">
				Draft proposals between calls, pulling CRM, pricing decks, and customer context
				automatically.
			</p>
		</article>

		<article class="bg-background min-h-72 p-8 md:col-span-2 md:p-10">
			<div class="text-soft-gray mb-8">
				<Wallet class="size-7" strokeWidth={1.5} />
			</div>
			<h3 class="text-primary m-0 text-xl leading-tight font-semibold tracking-[-0.02em]">
				Financial agents.
			</h3>
			<p class="text-soft m-0 mt-3 leading-[1.55]">
				Reconcile invoices and prepare month-end close overnight.
			</p>
		</article>

		<article class="bg-background min-h-72 p-8 md:col-span-2 md:p-10">
			<div class="text-soft-gray mb-8">
				<Settings2 class="size-7" strokeWidth={1.5} />
			</div>
			<h3 class="text-primary m-0 text-xl leading-tight font-semibold tracking-[-0.02em]">
				Ops agents.
			</h3>
			<p class="text-soft m-0 mt-3 leading-[1.55]">
				Keep internal systems in sync without anyone asking.
			</p>
		</article>

		<article class="bg-background min-h-72 p-8 md:col-span-2 md:p-10">
			<div class="text-soft-gray mb-8">
				<LineChart class="size-7" strokeWidth={1.5} />
			</div>
			<h3 class="text-primary m-0 text-xl leading-tight font-semibold tracking-[-0.02em]">
				Data agents.
			</h3>
			<p class="text-soft m-0 mt-3 leading-[1.55]">
				Create a structured brain from your data — queryable by anyone, in plain English.
			</p>
		</article>

		<article class="bg-background min-h-72 p-8 md:col-span-2 md:p-10">
			<div class="text-soft-gray mb-8">
				<Presentation class="size-7" strokeWidth={1.5} />
			</div>
			<h3 class="text-primary m-0 text-xl leading-tight font-semibold tracking-[-0.02em]">
				Design agents.
			</h3>
			<p class="text-soft m-0 mt-3 leading-[1.55]">
				Produce on-brand decks from a brief, pulling templates and assets from your brand library.
			</p>
		</article>
	</div>
</section>

<!-- "How we work" — three-step engagement model. Replaces the old Architecture section. -->
<section class="bg-surface-sunken border-border mt-24 border-y py-24 md:mt-32 md:py-32">
	<div class="mx-auto max-w-264 px-6 sm:px-10">
		<SectionHeader kicker="Engagement">
			{#snippet title()}
				How we work with you.
			{/snippet}
		</SectionHeader>

		<div class="mt-12 grid gap-6 md:mt-16 md:grid-cols-3 md:gap-8">
			<!-- Step 1 · Embed -->
			<article
				bind:this={howWeWorkCards[0]}
				class="bg-background border-border rounded-xl border p-6 md:p-8"
				style="opacity: {cardReveals[0]}; will-change: opacity;"
			>
				<div class="text-soft-gray mb-6 flex aspect-[5/3] items-center justify-center">
					<svg viewBox="0 0 200 120" class="size-full" fill="none" aria-hidden="true">
						<!-- Connections — draw in via stroke-dashoffset, subtle blue tint -->
						<g
							stroke-width="1"
							pathLength="1"
							stroke-dasharray="1"
							style="stroke: oklch(0.55 0.1 230); opacity: 0.55; stroke-dashoffset: {1 -
								card1LinesP};"
						>
							<line x1="62" y1="60" x2="115" y2="40" />
							<line x1="62" y1="60" x2="115" y2="80" />
							<line x1="115" y1="40" x2="155" y2="60" />
							<line x1="115" y1="80" x2="155" y2="60" />
							<line x1="115" y1="40" x2="115" y2="80" />
						</g>
						<!-- Team nodes (outline) — scale in after the lines have drawn -->
						<g class="fill-background stroke-current" stroke-width="1">
							<circle
								cx="115"
								cy="40"
								r="8"
								style="opacity: {card1NodesP}; transform: scale({0.3 +
									0.7 * card1NodesP}); transform-origin: 115px 40px;"
							/>
							<circle
								cx="115"
								cy="80"
								r="8"
								style="opacity: {card1NodesP}; transform: scale({0.3 +
									0.7 * card1NodesP}); transform-origin: 115px 80px;"
							/>
							<circle
								cx="155"
								cy="60"
								r="8"
								style="opacity: {card1NodesP}; transform: scale({0.3 +
									0.7 * card1NodesP}); transform-origin: 155px 60px;"
							/>
						</g>
						<!-- Engineer accent: primary dot with a subtle blue halo ring -->
						<!-- Continuous pulse ring — emanates outward from the engineer position. -->
						<circle
							cx="60"
							cy="60"
							r="10"
							fill="none"
							stroke-width="1"
							class="card-pulse-ring"
							style="stroke: oklch(0.55 0.12 230); opacity: {accentScales[0]};"
						/>
						<circle
							cx="60"
							cy="60"
							r="14"
							fill="none"
							stroke-width="1.25"
							style="stroke: oklch(0.55 0.12 230); opacity: {accentScales[0] *
								0.5}; transform: scale({0.4 + 0.6 * accentScales[0]}); transform-origin: 60px 60px;"
						/>
						<circle
							cx="60"
							cy="60"
							r="10"
							class="fill-primary"
							style="opacity: {accentScales[0]}; transform: scale({0.6 +
								0.4 * accentScales[0]}); transform-origin: 60px 60px;"
						/>
					</svg>
				</div>
				<div class="kicker">
					<span class="kicker-rule"></span>
					<span>01 — Embed</span>
				</div>
				<h3 class="text-primary mt-3 text-xl leading-tight font-semibold tracking-[-0.02em]">
					We embed with your team.
				</h3>
				<p class="text-soft mt-3 leading-[1.55]">
					An engineer on-site, mapping how the work gets done with your team.
				</p>
			</article>

			<!-- Step 2 · Build -->
			<article
				bind:this={howWeWorkCards[1]}
				class="bg-background border-border rounded-xl border p-6 md:p-8"
				style="opacity: {cardReveals[1]}; will-change: opacity;"
			>
				<div class="text-soft-gray mb-6 flex aspect-[5/3] items-center justify-center">
					<svg viewBox="0 0 200 120" class="size-full" fill="none" aria-hidden="true">
						<!-- Platform stack rows — fade up sequentially from bottom (Runtime) to top -->
						<g stroke-width="1" pathLength="1" style="stroke: oklch(0.65 0.1 60); opacity: 0.7;">
							<rect
								x="40"
								y="86"
								width="120"
								height="14"
								style="opacity: {card2Row1P}; transform: translateY({(1 - card2Row1P) *
									6}px); transform-box: fill-box;"
							/>
							<rect
								x="40"
								y="68"
								width="120"
								height="14"
								style="opacity: {card2Row2P}; transform: translateY({(1 - card2Row2P) *
									6}px); transform-box: fill-box;"
							/>
							<rect
								x="40"
								y="50"
								width="120"
								height="14"
								style="opacity: {card2Row3P}; transform: translateY({(1 - card2Row3P) *
									6}px); transform-box: fill-box;"
							/>
						</g>
						<!-- Vertical dividers — draw down after rows -->
						<g
							class="stroke-current"
							stroke-width="1"
							pathLength="1"
							stroke-dasharray="1"
							style="stroke-dashoffset: {1 - card2DivP}; opacity: {0.4 + 0.4 * card2DivP};"
						>
							<line x1="70" y1="50" x2="70" y2="100" />
							<line x1="100" y1="50" x2="100" y2="100" />
							<line x1="130" y1="50" x2="130" y2="100" />
						</g>
						<!-- Connector — extends from the agent down into the stack as it lands -->
						<line
							x1="100"
							y1="42"
							x2="100"
							y2="50"
							class="stroke-current"
							stroke-width="1"
							style="opacity: {accentScales[1]};"
						/>
						<!-- Amber halo behind the agent — subtle color hint -->
						<rect
							x="66"
							y="18"
							width="68"
							height="28"
							rx="5"
							fill="none"
							stroke-width="1.25"
							style="stroke: oklch(0.65 0.13 60); opacity: {accentScales[1] *
								0.45}; transform: translateY({(1 - accentScales[1]) *
								-12}px); transform-box: fill-box;"
						/>
						<!-- Agent on top (filled, accent) — drops in from above -->
						<rect
							x="70"
							y="22"
							width="60"
							height="20"
							rx="3"
							class="fill-primary"
							style="opacity: {accentScales[1]}; transform: translateY({(1 - accentScales[1]) *
								-16}px); transform-box: fill-box;"
						/>
						<!-- Continuous flow — a small dot drops from agent into the stack, repeating. -->
						<circle
							cx="100"
							cy="42"
							r="1.75"
							class="card-flow-dot"
							style="fill: oklch(0.6 0.13 60); opacity: {accentScales[1]};"
						/>
					</svg>
				</div>
				<div class="kicker">
					<span class="kicker-rule"></span>
					<span>02 — Build</span>
				</div>
				<h3 class="text-primary mt-3 text-xl leading-tight font-semibold tracking-[-0.02em]">
					We build the agents on Hessian.
				</h3>
				<p class="text-soft mt-3 leading-[1.55]">
					Code-first, on our platform. Tools, policies, runtime, observability — already baked in
					from day one.
				</p>
			</article>

			<!-- Step 3 · Operate -->
			<article
				bind:this={howWeWorkCards[2]}
				class="bg-background border-border rounded-xl border p-6 md:p-8"
				style="opacity: {cardReveals[2]}; will-change: opacity;"
			>
				<div class="text-soft-gray mb-6 flex aspect-[5/3] items-center justify-center">
					<svg viewBox="0 0 200 120" class="size-full" fill="none" aria-hidden="true">
						<!-- Continuous track — draws left to right first -->
						<line
							x1="18"
							y1="82"
							x2="182"
							y2="82"
							class="stroke-current"
							stroke-width="1"
							stroke-dasharray="2 3"
							pathLength="1"
							style="stroke-dashoffset: {1 - card3TrackP};"
						/>
						<!-- Verticals connecting bars to track — draw down after track -->
						<g
							class="stroke-current"
							stroke-width="1"
							pathLength="1"
							stroke-dasharray="1"
							style="stroke-dashoffset: {1 - card3VertP};"
						>
							<line x1="35" y1="46" x2="35" y2="74" />
							<line x1="65" y1="46" x2="65" y2="74" />
							<line x1="95" y1="46" x2="95" y2="74" />
							<line x1="125" y1="46" x2="125" y2="74" />
							<line x1="155" y1="46" x2="155" y2="74" />
						</g>
						<!-- Telemetry bars — rise from their bottom edges in left-to-right cascade.
							 Subtle green tint hints at "healthy run" without being loud. -->
						<g style="fill: oklch(0.55 0.1 150); opacity: 0.55;">
							<rect
								x="32"
								y="28"
								width="6"
								height="14"
								style="transform: scaleY({card3BarPs[0]}); transform-origin: 35px 42px;"
							/>
							<rect
								x="62"
								y="35"
								width="6"
								height="7"
								style="transform: scaleY({card3BarPs[1]}); transform-origin: 65px 42px;"
							/>
							<rect
								x="92"
								y="22"
								width="6"
								height="20"
								style="transform: scaleY({card3BarPs[2]}); transform-origin: 95px 42px;"
							/>
							<rect
								x="122"
								y="32"
								width="6"
								height="10"
								style="transform: scaleY({card3BarPs[3]}); transform-origin: 125px 42px;"
							/>
							<rect
								x="152"
								y="26"
								width="6"
								height="16"
								style="transform: scaleY({card3BarPs[4]}); transform-origin: 155px 42px;"
							/>
						</g>
						<!-- Past-run markers — fill in sequentially with a subtle scale pop. -->
						<circle
							cx="35"
							cy="82"
							r="5"
							class="fill-primary"
							style="opacity: {card3DotPs[0]}; transform: scale({0.4 +
								0.6 * card3DotPs[0]}); transform-origin: 35px 82px;"
						/>
						<circle
							cx="65"
							cy="82"
							r="5"
							class="fill-primary"
							style="opacity: {card3DotPs[1]}; transform: scale({0.4 +
								0.6 * card3DotPs[1]}); transform-origin: 65px 82px;"
						/>
						<circle
							cx="95"
							cy="82"
							r="5"
							class="fill-primary"
							style="opacity: {card3DotPs[2]}; transform: scale({0.4 +
								0.6 * card3DotPs[2]}); transform-origin: 95px 82px;"
						/>
						<g class="fill-background stroke-current" stroke-width="1">
							<circle cx="125" cy="82" r="5" />
							<circle cx="155" cy="82" r="5" />
						</g>
						<!-- Heartbeat — subtle green ring pulses from the latest past-run dot,
							 hinting at "live, still running" without animating the dot itself. -->
						<circle
							cx="95"
							cy="82"
							r="5"
							fill="none"
							stroke-width="1"
							class="card-heartbeat"
							style="stroke: oklch(0.55 0.1 150); opacity: {card3DotPs[2]};"
						/>
					</svg>
				</div>
				<div class="kicker">
					<span class="kicker-rule"></span>
					<span>03 — Operate</span>
				</div>
				<h3 class="text-primary mt-3 text-xl leading-tight font-semibold tracking-[-0.02em]">
					We run and operate them in production.
				</h3>
				<p class="text-soft mt-3 leading-[1.55]">
					Every day. Every workflow. We partner with you long-term to meet your needs.
				</p>
			</article>
		</div>
	</div>
</section>

<!-- About us — small team intro before the closing manifesto. -->
<section class="mx-auto max-w-264 px-6 py-32 sm:px-10 md:py-40">
	<div class="kicker mb-10">
		<span class="kicker-rule"></span>
		<span>About us</span>
	</div>

	<!-- Team — square photos with name + LinkedIn icon underneath each. -->
	<div class="mb-10 flex flex-wrap gap-6">
		<a
			href="https://www.linkedin.com/in/bao-thai-nguyen"
			target="_blank"
			rel="noopener noreferrer"
			aria-label="Bao on LinkedIn"
			class="group block"
		>
			<enhanced:img
				src={Bao}
				alt=""
				class="ring-border group-hover:ring-primary size-20 object-cover ring-1 transition-shadow"
			/>
			<div
				class="text-soft-gray group-hover:text-primary mt-3 flex items-center gap-1.5 text-sm transition-colors"
			>
				<span class="text-primary font-medium">Bao</span>
				<Linkedin class="size-3.5" />
			</div>
		</a>
		<a
			href="https://www.linkedin.com/in/jamesdsmith99"
			target="_blank"
			rel="noopener noreferrer"
			aria-label="James on LinkedIn"
			class="group block"
		>
			<enhanced:img
				src={James}
				alt=""
				class="ring-border group-hover:ring-primary size-20 object-cover ring-1 transition-shadow"
			/>
			<div
				class="text-soft-gray group-hover:text-primary mt-3 flex items-center gap-1.5 text-sm transition-colors"
			>
				<span class="text-primary font-medium">James</span>
				<Linkedin class="size-3.5" />
			</div>
		</a>
		<a
			href="https://www.linkedin.com/in/thomasedw"
			target="_blank"
			rel="noopener noreferrer"
			aria-label="Tom on LinkedIn"
			class="group block"
		>
			<enhanced:img
				src={Tom}
				alt=""
				class="ring-border group-hover:ring-primary size-20 object-cover ring-1 transition-shadow"
			/>
			<div
				class="text-soft-gray group-hover:text-primary mt-3 flex items-center gap-1.5 text-sm transition-colors"
			>
				<span class="text-primary font-medium">Tom</span>
				<Linkedin class="size-3.5" />
			</div>
		</a>
	</div>

	<blockquote
		class="text-primary m-0 max-w-220 font-medium tracking-[-0.015em]"
		style="font-size: clamp(1.25rem, 2vw, 1.625rem); line-height: 1.4;"
	>
		We're three engineers with a decade of shipping production infrastructure. Hessian is here to
		help you deliver business value where it matters.
	</blockquote>

	<p class="text-soft-gray mt-6 text-sm">Founders, Hessian Inc.</p>
</section>

<!-- Manifesto CTA band — thesis statement + mission continuation. -->
<section class="border-border border-y py-32 md:py-48">
	<div class="mx-auto max-w-264 px-6 sm:px-10">
		<h2
			class="text-primary m-0 max-w-240 font-semibold tracking-[-0.035em]"
			style="font-size: clamp(2rem, 4.5vw, 4rem); line-height: 1.05;"
		>
			Every knowledge worker is about to operate like
			<span class="serif-ital">a team of a hundred.</span>
		</h2>

		<p
			class="text-soft-gray mt-8 max-w-200 font-medium tracking-[-0.02em]"
			style="font-size: clamp(1.25rem, 2.2vw, 1.875rem); line-height: 1.3;"
		>
			We're building the company that makes it possible.
		</p>

		<div class="mt-12 flex flex-wrap items-center gap-2.5 md:mt-16">
			<Button
				href="https://calendar.app.google/6dgXubAvkt54JReg8"
				target="_blank"
				rel="noopener noreferrer"
				class="bg-primary text-background hover:bg-primary/90 inline-flex h-11 items-center justify-center gap-2 rounded-md px-6 text-[15px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)] transition-colors"
			>
				{m.index_announcement_button()}
				<ArrowRight class="size-3.5" />
			</Button>

			<Button
				href="https://github.com/hessianhq"
				variant="secondary"
				class="border-border bg-surface-raised text-primary hover:bg-surface-hover inline-flex h-11 items-center justify-center gap-2 rounded-md border px-6 text-[15px] font-medium transition-colors"
			>
				<Github />
				Follow on GitHub
			</Button>
		</div>
	</div>
</section>

<div class="md:h-20"></div>

<style>
	@keyframes mobile-dag-pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.35;
		}
	}

	.scroll-hint-arrow {
		animation: scroll-hint-bounce 2.4s ease-in-out infinite;
	}
	@keyframes scroll-hint-bounce {
		0%,
		100% {
			transform: translateY(0);
		}
		50% {
			transform: translateY(4px);
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.scroll-hint-arrow {
			animation: none;
		}
	}

	/* Post-scroll continuous animations on the engagement cards' accents.
		 Subtle, low-amplitude — each card gets a different motion that
		 reinforces its meaning (presence, flow, heartbeat). */

	/* Card 1 — pulse ring expands outward from the engineer dot. */
	.card-pulse-ring {
		transform-origin: 60px 60px;
		animation: card-pulse 3.6s ease-out infinite;
	}
	@keyframes card-pulse {
		0% {
			transform: scale(0.85);
			opacity: 0;
		}
		15% {
			opacity: 0.5;
		}
		100% {
			transform: scale(2.6);
			opacity: 0;
		}
	}

	/* Card 2 — small dot drops down the connector from agent into the stack. */
	.card-flow-dot {
		animation: card-flow 2.8s ease-in-out infinite;
	}
	@keyframes card-flow {
		0% {
			transform: translateY(0);
			opacity: 0;
		}
		20% {
			opacity: 1;
		}
		60% {
			opacity: 1;
		}
		100% {
			transform: translateY(48px);
			opacity: 0;
		}
	}

	/* Card 3 — heartbeat ring pulses around the latest past-run dot. */
	.card-heartbeat {
		transform-origin: 95px 82px;
		animation: card-heartbeat 2.4s ease-out infinite;
	}
	@keyframes card-heartbeat {
		0% {
			transform: scale(1);
			opacity: 0;
		}
		20% {
			opacity: 0.65;
		}
		100% {
			transform: scale(2.4);
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.card-pulse-ring,
		.card-flow-dot,
		.card-heartbeat {
			animation: none;
		}
	}
</style>
